import {
  setHierarchy,
  getFlattenedOptions,
  getDefaultValueAsArray,
} from './utils';

/*

A note about naming...

The `value` and `defaultValue` variables coming from React Select library are
either an object or an array of objects containing the select options.

In this component, we do a lot of sorting and filtering of these options and
it's handy to store variables that just contain the IDs of particular options.

So, in our custom variables (but not in the React Select library variables) we
follow this pattern:

- "*Values" is an array of strings, each containing a unique id, e.g
  ['d014f1e2-cb0b-4562-a7a4-6c568a9cf377']
- "*Options" is an array of objects, each containing the whole option, e.g.
  [{ value, label, isParent, children... }]

Examples:
- selectedValues = [1, 2, 3]
- selectedOptions = [{ a, b, c }]

*/

// Get data to create multiple selects from the props passed into the components
export const getSelectsFromProps = (options, defaultValue) => {
  const selectedOptions = getDefaultValueAsArray(defaultValue);
  const selectedValues = selectedOptions.map((o) => o.value);

  // Mark up the options with the necessary data, then flatten
  const markedOptions = setHierarchy(options, false);
  const flattenedOptions = getFlattenedOptions(markedOptions);

  const uniqueSelectedOptions = markedOptions.filter((o) =>
    selectedValues.includes(o.value),
  );
  const uniqueSelectedValues = uniqueSelectedOptions.map((o) => o.value);

  return selectedValues
    .map((val) => {
      const option = flattenedOptions.find((o) => o.value === val);

      if (option) {
        // We only return parent and single options. Children options are handled later on by the ConditionalSelect component.
        if (!option.isChild) {
          return {
            key: option.value,
            options: options.filter(
              (o) =>
                !uniqueSelectedValues.includes(o.value) ||
                o.value === option.value,
            ),
            defaultValue: option.isParent
              ? [
                  option,
                  ...selectedValues
                    .map((o) => {
                      const flatOpt = flattenedOptions.find(
                        (opt) => opt.value === o,
                      );
                      if (flatOpt?.childOf === option.value) {
                        return flatOpt;
                      }
                    })
                    .filter((x) => typeof x !== 'undefined'),
                ]
              : [option],
          };
        }
      } else {
        console.warn('Selected value not found in options.');
      }
    })
    .filter((x) => typeof x !== 'undefined');
};

export const getGroupValueFromSelectedOptions = (options) => {
  // "Group value" is the value that will be written to a hidden input for
  // form submission. Here's the current logic:
  //   1. Include parent if none of its children are selected.
  //   2. If any children are selected include the children and exclude parent.
  // This is currently the only use case. In the future this may be made configurable.
  const parentsToRemove = options
    .map((o) => o.childOf)
    .filter((o) => o !== 'undefined');

  return options
    .filter((o) => !parentsToRemove.includes(o.value))
    .map((o) => o.value);
};

export const canShowAddMore = (selects) => {
  const selectWithoutValue = !!selects.find((o) => !o?.defaultValue?.length);

  return !selects.length || selectWithoutValue ? false : true;
};

const reducer = (state, action) => {
  const { options, defaultValue, selects } = state;
  const { type, val } = action;
  const markedOptions = setHierarchy(options, false);
  const flattenedOptions = getFlattenedOptions(markedOptions);
  let updatedSelects, updatedSelectedOptions;

  switch (type) {
    case 'ADD_SELECT':
      const defaultIds = defaultValue.map((o) => o.value);
      const availableOptions = options.filter(
        (o) => !defaultIds.includes(o.value),
      );

      updatedSelects = [...selects, { options: availableOptions }];

      const updatedState = {
        ...state,
        options,
        defaultValue,
        selects: updatedSelects,
        showAddMore: canShowAddMore(updatedSelects),
      };

      return updatedState;

    case 'SELECT_CHANGE':
      if (val) {
        const { value, inputAction, previousValue } = val;
        const { action, removedValue, removedValues } = inputAction;

        updatedSelectedOptions = defaultValue;

        const removeSelectedChildrenByParent = (
          parent,
          options,
          selectedOptions,
        ) => {
          const childValues = options
            .filter((o) => o.childOf === parent.value)
            .map((o) => o.value);

          // Remove previous value's child options
          return selectedOptions.filter((o) => !childValues.includes(o.value));
        };

        switch (action) {
          case 'select-option':
            // `value[0].isChild` checks both "is it multi?" (single-selects are not in an array) and
            // "is it a child?" (if one of the values has `isChild` they all will).
            const isMultiChild = value.length && value[0].isChild;

            if (isMultiChild) {
              const childValues = value.map((o) => o.value);
              const selectedValues = updatedSelectedOptions.map((o) => o.value);
              const newValues = childValues.filter(
                (v) => !selectedValues.includes(v),
              );
              const newOptions = flattenedOptions.filter((o) =>
                newValues.includes(o.value),
              );
              updatedSelectedOptions.push(...newOptions);
            } else {
              if (previousValue) {
                // In a single-select, when a value changes, get the previous value and
                // remove that value's children from the list of selected options
                if (previousValue.isParent) {
                  updatedSelectedOptions = removeSelectedChildrenByParent(
                    previousValue,
                    flattenedOptions,
                    updatedSelectedOptions,
                  );
                }

                // Replace previous value with current value
                const index = updatedSelectedOptions.findIndex(
                  (o) => o.value === previousValue.value,
                );

                if (index !== -1) {
                  updatedSelectedOptions[index] = value;
                } else {
                  console.warn(
                    'Something went wrong. `previousValue` should always match a value in `updatedSelectedOptions`.',
                  );
                }
              } else {
                updatedSelectedOptions.push(value);
              }
            }

            break;
          case 'clear':
            const removedValuesArr = removedValues.map(
              (option) => option.value,
            );

            updatedSelectedOptions = updatedSelectedOptions.filter(
              (option) => !removedValuesArr.includes(option.value),
            );

            break;
          case 'remove-value':
            updatedSelectedOptions = updatedSelectedOptions.filter(
              (option) => option.value !== removedValue.value,
            );

            break;
        }
      }

      updatedSelects = getSelectsFromProps(options, updatedSelectedOptions);

      return {
        ...state,
        defaultValue: updatedSelectedOptions,
        groupValue: getGroupValueFromSelectedOptions(updatedSelectedOptions),
        selects: updatedSelects,
        showAddMore: canShowAddMore(updatedSelects),
      };

    default:
      console.warn(`"${type}" is not a valid type`);
  }
};

export default reducer;
